import React, { FC, useId } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useBackgroundColor } from '../../utils';
import { Typography } from '../typography/Typography';
import { Stack } from '../stack/Stack';
import { TickCircle } from '../icon';

// Interface

export interface TrustPilotReviewsProps {
  reviews?: TrustpilotReviewProps[];
  reviewTag?: {
    review1?: {
      name?: string;
    };
  };
}

export interface TrustpilotReviewProps {
  heading?: string;
  text?: string;
  rating?: number;
  reviewerName?: string;
  date?: string;
  verified?: boolean;
  id?: string;
}

export interface StarsProps {
  stars: number;
}

// Styles

const StyledTrustPilotReviews = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.medium};
    color: ${colors.trustPilotBlack};

    @media (min-width: ${media.large}) {
      flex-direction: row;
      gap: ${space.large};
    }
  `};
`;

const StyledTrustPilotReview = styled.div`
  ${({ theme: { space, media, borderRadii } }) => css`
    padding: ${space.large};
    border-radius: ${borderRadii.medium};
    ${useBackgroundColor('utilityWhite')};
    width: 100%;

    @media (min-width: ${media.large}) {
      width: 33%;
    }
  `};
`;

const StyledRatingBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const StyledVerified = styled.div`
  ${({ theme: { space, colors } }) => css`
    color: ${colors.trustPilotGrey};
    display: flex;
    gap: ${space.xxxSmall};
    align-items: center;
  `};
`;

const StyledTitle = styled(Typography)`
  ${({ theme: { colors } }) => css`
    color: ${colors.trustPilotBlack};
  `};
`;

const StyledReview = styled(Typography)`
  ${({ theme: { colors } }) => css`
    color: ${colors.trustPilotBlack};
  `};
`;

const StyledName = styled(Typography)`
  ${({ theme: { colors } }) => css`
    color: ${colors.trustPilotGrey};
  `};
`;

const StyledDate = styled(Typography)`
  ${({ theme: { colors } }) => css`
    color: ${colors.trustPilotGrey};
  `};
`;

// JSX

const Stars: React.FC<StarsProps> = ({ stars }) => {
  const fullStars = Math.floor(stars);
  const halfStar = stars % 1 >= 0.5;
  const id = useId();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24 * 5 + 4}
      height="24"
      fill="none"
      aria-describedby={id}
    >
      <title id={id}>Rating: {stars} stars out of 5</title>
      {Array.from({ length: fullStars }).map((_, index) => (
        <g key={index} transform={`translate(${index * 25})`}>
          <path fill="#00B67A" d="M0 0h24v24H0z" />
          <path
            fill="#fff"
            d="m12 15.939 3.476-.873L16.93 19.5 12 15.939Zm8-5.732h-6.119L12 4.5l-1.881 5.707H4l4.952 3.538-1.88 5.708 4.952-3.538 3.047-2.17L20 10.208Z"
          />
        </g>
      ))}
      {halfStar ? (
        <g transform={`translate(${fullStars * 25})`}>
          <path fill="#D9D9D9" d="M0 0h24v24H0z" />
          <path fill="#00B67A" d="M.387 0h12v24h-12z" />
          <path
            fill="#fff"
            d="m12 15.939 3.476-.873L16.93 19.5 12 15.939Zm8-5.732h-6.119L12 4.5l-1.881 5.707H4 l4.952 3.538-1.88 5.708 4.952-3.538 3.047-2.17L20 10.208Z"
          />
        </g>
      ) : null}
      {Array.from({ length: 5 - Math.ceil(stars) }).map((_, index) => (
        <g
          key={index + stars}
          transform={`translate(${
            (fullStars + (halfStar ? 1 : 0)) * 25 + index * 25
          })`}
        >
          <path fill="#D9D9D9" d="M0 0h24v24H0z" />
          <path
            fill="#fff"
            d="m12 15.939 3.476-.873L16.93 19.5 12 15.939Zm8-5.732h-6.119L12 4.5l-1.881 5.707H4 l4.952 3.538-1.88 5.708 4.952-3.538 3.047-2.17L20 10.208Z"
          />
        </g>
      ))}
    </svg>
  );
};

const formatDate = (dateString) => {
  const options = { day: '2-digit', month: 'long' }; // Format options
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', options); // 'en-GB' ensures the format is day/month
};

const ReviewCard: FC<TrustpilotReviewProps> = ({
  heading,
  text,
  rating,
  reviewerName,
  date,
  verified,
}) => {
  const formattedDate = date ? formatDate(date) : null;
  return (
    <StyledTrustPilotReview as="li">
      <Stack gap="large">
        <StyledRatingBar>
          <Stars stars={rating} />
          <StyledVerified>
            {verified && (
              <>
                <TickCircle size="xSmall" />
                <Typography variant="bodySmall">Verified</Typography>
              </>
            )}
          </StyledVerified>
        </StyledRatingBar>
        <Stack gap="small">
          {heading && (
            <StyledTitle variant="bodyLargeBold" component="h3">
              {heading}
            </StyledTitle>
          )}
          {text && <StyledReview variant="bodyMedium">{text}</StyledReview>}
          <div>
            {reviewerName && (
              <StyledName variant="bodySmallBold">{reviewerName}</StyledName>
            )}
            {date && (
              <StyledDate variant="bodySmall">{formattedDate}</StyledDate>
            )}
          </div>
        </Stack>
      </Stack>
    </StyledTrustPilotReview>
  );
};

export const TrustPilotReviews: FC<TrustPilotReviewsProps> = ({
  reviews = [],
}) => (
  <StyledTrustPilotReviews as="ul">
    {reviews.map((review) => (
      <ReviewCard key={review.id} {...review} />
    ))}
  </StyledTrustPilotReviews>
);

export default TrustPilotReviews;
